import { FiEdit3 } from 'react-icons/fi';

export const complexSentenceWritingData = {
  courseId: 'complex-sentence-writing',
  courseType: 'writing-course',
  title: 'Complex Sentence Patterns for Writing',
  icon: FiEdit3,
  overview: {
    impact: 'Demonstrate grammatical range for Band 8-9 scores',
    focus: 'Relative clauses, participle clauses, nominalization, cleft sentences, conditionals, and passive voice',
    uses: 'IELTS Writing Tasks 1 and 2 — achieving the "wide range of structures" criterion',
    duration: '25-30 minutes'
  },
  sections: [
    {
      title: 'Relative & Participle Clauses',
      content: `<p>Relative and participle clauses let you <strong>combine information</strong> that would otherwise require separate simple sentences. This demonstrates grammatical range — a key Band 8+ criterion.</p>
<p><strong>Relative clauses</strong> add information about a noun using who, which, that, where, or whose:</p>
<ul>
  <li><strong>Defining:</strong> <em>"Students <u>who study abroad</u> develop stronger intercultural skills."</em> (essential info — no commas)</li>
  <li><strong>Non-defining:</strong> <em>"Japan, <u>which has the world's oldest population</u>, faces a shrinking workforce."</em> (extra info — with commas)</li>
</ul>
<p><strong>Participle clauses</strong> use -ing or -ed forms to compress information:</p>
<ul>
  <li><strong>Present participle:</strong> <em>"<u>Facing rising costs</u>, many families are reducing discretionary spending."</em></li>
  <li><strong>Past participle:</strong> <em>"<u>Founded in 1945</u>, the United Nations has 193 member states."</em></li>
  <li><strong>Perfect participle:</strong> <em>"<u>Having analysed the data</u>, the researchers drew three conclusions."</em></li>
</ul>`,
      points: [
        'Non-defining relative clauses (with commas) are more academic and show control of punctuation.',
        'Participle clauses are especially useful for IELTS Task 1 when describing trends and processes.',
        'Avoid stacking multiple relative clauses in one sentence — it creates confusion, not sophistication.',
        'The subject of a participle clause must match the subject of the main clause (avoid dangling modifiers).'
      ]
    },
    {
      title: 'Nominalization Techniques',
      content: `<p>Nominalization converts <strong>verbs and adjectives into nouns</strong>, making writing more formal and information-dense. It is one of the most important features of academic English.</p>
<p><strong>Common patterns:</strong></p>
<table>
  <tr><th>Verb/Adjective</th><th>Nominalized Form</th></tr>
  <tr><td>develop</td><td>the <u>development</u> of</td></tr>
  <tr><td>important</td><td>the <u>importance</u> of</td></tr>
  <tr><td>fail</td><td>the <u>failure</u> to</td></tr>
  <tr><td>decide</td><td>the <u>decision</u> to</td></tr>
  <tr><td>participate</td><td><u>participation</u> in</td></tr>
  <tr><td>unemployed</td><td><u>unemployment</u></td></tr>
</table>
<p><strong>Before:</strong> <em>"People are concerned because the economy is declining."</em> (informal, verb-heavy)<br/>
<strong>After:</strong> <em>"<u>Concern over the economic decline</u> has intensified."</em> (academic, noun-heavy)</p>
<p><strong>Before:</strong> <em>"The government decided to invest more in schools."</em><br/>
<strong>After:</strong> <em>"The government's <u>decision to increase investment</u> in education…"</em></p>`,
      points: [
        'Nominalization is the single most effective way to shift your register from informal to academic.',
        'Use it in topic sentences and thesis statements for maximum impact.',
        'Do not nominalize every sentence — overuse makes writing impenetrable. Aim for 2-3 per paragraph.',
        'Common suffixes: -tion, -ment, -ness, -ity, -ence, -ance, -al, -ure.'
      ]
    },
    {
      title: 'Cleft Sentences & Emphasis',
      content: `<p>Cleft sentences restructure information to <strong>emphasise a specific element</strong>. They are powerful in IELTS essays for drawing the examiner's attention to your key point.</p>
<p><strong>Types of cleft sentences:</strong></p>
<ul>
  <li><strong>It-cleft:</strong> <em>"<u>It is education</u> that drives long-term economic growth."</em> (emphasises "education")</li>
  <li><strong>What-cleft (pseudo-cleft):</strong> <em>"<u>What developing nations need most</u> is investment in infrastructure."</em> (emphasises "investment in infrastructure")</li>
  <li><strong>All-cleft:</strong> <em>"<u>All the evidence suggests</u> that early intervention is most effective."</em></li>
  <li><strong>The reason…is that:</strong> <em>"<u>The reason</u> many students fail <u>is that</u> they lack effective study strategies."</em></li>
</ul>
<p><strong>Normal:</strong> <em>"Poverty causes educational inequality."</em><br/>
<strong>It-cleft:</strong> <em>"It is poverty that is the root cause of educational inequality."</em><br/>
<strong>What-cleft:</strong> <em>"What primarily drives educational inequality is poverty."</em></p>`,
      points: [
        'Use cleft sentences for your thesis statement or the key argument in a body paragraph — one or two per essay.',
        'What-clefts are excellent for conclusions: "What this essay has demonstrated is that…"',
        'Over-using cleft sentences sounds artificial — they are emphasis tools, not default structures.',
        '"The reason…is that…" is useful for cause-effect arguments but avoid "The reason is because" (grammatically incorrect).'
      ]
    },
    {
      title: 'Conditional & Passive Patterns',
      content: `<p><strong>Conditional structures</strong> in essays express hypothetical situations, predictions, and logical consequences:</p>
<ul>
  <li><strong>Zero conditional (facts):</strong> <em>"If governments reduce education budgets, literacy rates decline."</em></li>
  <li><strong>First conditional (real future):</strong> <em>"If this trend continues, urban areas will face severe housing shortages."</em></li>
  <li><strong>Second conditional (hypothetical):</strong> <em>"If every country invested 5% of GDP in renewable energy, global emissions would fall significantly."</em></li>
  <li><strong>Third conditional (past hypothetical):</strong> <em>"If early action had been taken, the crisis could have been averted."</em></li>
  <li><strong>Mixed conditional:</strong> <em>"If the government had invested in education decades ago, the workforce would be more skilled today."</em></li>
</ul>
<p><strong>Passive voice</strong> shifts focus from the agent to the action or result, which is appropriate when:</p>
<ul>
  <li>The agent is unknown or unimportant: <em>"The survey <u>was conducted</u> in 2023."</em></li>
  <li>You want an objective tone: <em>"It <u>can be argued</u> that…"</em></li>
  <li>Describing processes (Task 1): <em>"The raw materials <u>are transported</u> to the factory."</em></li>
</ul>`,
      points: [
        'Second conditionals are powerful for proposing solutions: "If governments were to implement X, Y would improve."',
        'Mix active and passive voice — an entirely passive essay sounds evasive; an entirely active one sounds informal.',
        'Use passive when the "doer" is less important than the action or the receiver.',
        'Conditional structures work well in conclusions to suggest future implications of your argument.'
      ]
    }
  ],
  exercises: [
    {
      title: 'Combine Sentences Using Relative Clauses',
      type: 'fill-in',
      instructions: 'Combine the two sentences using a relative clause. Type the connecting relative word (who, which, that, where, or whose).',
      questions: [
        {
          text: '"The city has a population of 10 million. The city was founded in 1850." → "The city, ___ was founded in 1850, has a population of 10 million."',
          answer: 'which',
          explanation: '"Which" introduces a non-defining relative clause (set off by commas) adding extra information about "the city." Use "which" for things and "who" for people in non-defining clauses.'
        },
        {
          text: '"Students pass the exam. Students study consistently." → "Students ___ study consistently pass the exam."',
          answer: 'who',
          explanation: '"Who" introduces a defining relative clause (no commas) specifying which students. "That" is also acceptable in defining clauses but "who" is preferred for people in formal writing.'
        },
        {
          text: '"Dr. Lee published the research. Dr. Lee\'s findings changed the field." → "Dr. Lee, ___ findings changed the field, published the research."',
          answer: 'whose',
          explanation: '"Whose" is the possessive relative pronoun, replacing "Dr. Lee\'s." It works for both people and things in academic writing.'
        },
        {
          text: '"Helsinki is a city. Work-life balance is prioritised in Helsinki." → "Helsinki is a city ___ work-life balance is prioritised."',
          answer: 'where',
          explanation: '"Where" introduces a relative clause about a place. Alternatively, "in which" is more formal: "Helsinki is a city in which work-life balance is prioritised."'
        }
      ]
    },
    {
      title: 'Nominalize Verbs into Nouns',
      type: 'fill-in',
      instructions: 'Convert the underlined verb or adjective into its nominalized form.',
      questions: [
        {
          text: '"People PARTICIPATE in community events." → "___ in community events has increased."',
          answer: 'Participation',
          explanation: '"Participate" (verb) nominalizes to "participation" (noun). The nominalized version is more formal and information-dense.'
        },
        {
          text: '"The government DECIDED to fund the project." → "The government\'s ___ to fund the project was welcomed."',
          answer: 'decision',
          explanation: '"Decided" (verb) becomes "decision" (noun). Notice how the sentence shifts from active narration to a more formal, static style typical of academic writing.'
        },
        {
          text: '"It is IMPORTANT to protect the environment." → "The ___ of protecting the environment cannot be overstated."',
          answer: 'importance',
          explanation: '"Important" (adjective) nominalizes to "importance" (noun). This construction ("The importance of X") is extremely common in IELTS academic writing.'
        },
        {
          text: '"Researchers DISCOVERED a new treatment." → "The ___ of a new treatment was announced."',
          answer: 'discovery',
          explanation: '"Discovered" (verb) becomes "discovery" (noun). Note the irregular nominalization — not all nouns follow the simple "-tion" pattern.'
        }
      ]
    },
    {
      title: 'Identify Sentence Types',
      type: 'select',
      instructions: 'Identify the type of complex structure used in each sentence.',
      questions: [
        {
          text: '"It is the lack of funding that prevents progress in rural healthcare."',
          options: [
            'Relative clause',
            'It-cleft sentence',
            'Passive voice',
            'Nominalization'
          ],
          answer: 'It-cleft sentence',
          explanation: '"It is X that Y" is the classic it-cleft pattern. It emphasises "the lack of funding" as the key factor. Without the cleft: "The lack of funding prevents progress in rural healthcare."'
        },
        {
          text: '"Having reviewed the evidence, the committee recommended stricter regulations."',
          options: [
            'Non-defining relative clause',
            'Perfect participle clause',
            'Second conditional',
            'What-cleft sentence'
          ],
          answer: 'Perfect participle clause',
          explanation: '"Having + past participle" creates a perfect participle clause that shows one action (reviewing) happened before another (recommending). It is more concise than "After the committee had reviewed the evidence, it recommended…"'
        },
        {
          text: '"If governments were to invest more in public transport, congestion would decrease significantly."',
          options: [
            'Zero conditional',
            'First conditional',
            'Second conditional',
            'Third conditional'
          ],
          answer: 'Second conditional',
          explanation: '"Were to invest" (past subjunctive) + "would decrease" is a second conditional structure expressing a hypothetical — the speaker is imagining a scenario, not predicting a likely outcome.'
        }
      ]
    },
    {
      title: 'Rewrite as Complex Sentences',
      type: 'writing',
      instructions: 'Rewrite each simple sentence using the specified complex structure.',
      questions: [
        {
          text: 'Rewrite using a participle clause: "The policy was introduced in 2020. It has reduced unemployment by 15%."',
          example: 'Introduced in 2020, the policy has reduced unemployment by 15%.',
          tip: 'Use a past participle (-ed) at the start to compress the first sentence into a modifier. Make sure the subject of the participle matches the subject of the main clause.'
        },
        {
          text: 'Rewrite using nominalization: "People consume too much energy. This damages the environment."',
          example: 'Excessive energy consumption has caused significant environmental damage.',
          tip: 'Convert the verbs "consume" → "consumption" and "damages" → "damage" (noun). Add academic adjectives (excessive, significant) to increase formality.'
        },
        {
          text: 'Rewrite using a what-cleft for emphasis: "Developing nations need sustainable infrastructure investment above all."',
          example: 'What developing nations need above all is sustainable infrastructure investment.',
          tip: 'Move the most important element to the end of the sentence (the stress position) using "What… is…" to build emphasis.'
        }
      ]
    },
    {
      title: 'Choose the Most Academic Version',
      type: 'select',
      instructions: 'Select the sentence that demonstrates the most sophisticated grammatical range for IELTS academic writing.',
      questions: [
        {
          text: 'Which is most academic?',
          options: [
            'People get sick because the air is dirty.',
            'Dirty air makes people sick.',
            'The deterioration of air quality has been linked to a marked increase in respiratory illness.',
            'Air pollution causes sickness in people around the world.'
          ],
          answer: 'The deterioration of air quality has been linked to a marked increase in respiratory illness.',
          explanation: 'This uses nominalization ("deterioration," "increase"), passive voice ("has been linked to"), and precise academic vocabulary ("marked," "respiratory illness"). It is information-dense and formal.'
        },
        {
          text: 'Which sentence best demonstrates grammatical range?',
          options: [
            'Many people think education is important.',
            'Education is considered important by many.',
            'It is widely acknowledged that education, which forms the foundation of social mobility, plays a pivotal role in economic development.',
            'Education is important. Many people agree with this.'
          ],
          answer: 'It is widely acknowledged that education, which forms the foundation of social mobility, plays a pivotal role in economic development.',
          explanation: 'This sentence combines passive voice ("is widely acknowledged"), a non-defining relative clause ("which forms…"), and sophisticated vocabulary — demonstrating the "wide range of structures" needed for Band 8+.'
        },
        {
          text: 'Which version is best for an IELTS Task 1 process description?',
          options: [
            'Workers heat the metal and then they shape it into components.',
            'The metal is heated to 500°C before being shaped into precision components.',
            'First they heat the metal. Then they shape it.',
            'Heating the metal happens first, and shaping comes next.'
          ],
          answer: 'The metal is heated to 500°C before being shaped into precision components.',
          explanation: 'Task 1 processes should use passive voice (the agent is irrelevant), specific data ("500°C"), and complex linking ("before being shaped" — a passive participle construction). This demonstrates the concise, impersonal style examiners expect.'
        },
        {
          text: 'Which conditional structure is most appropriate for a Task 2 conclusion?',
          options: [
            'If the government does nothing, bad things will happen.',
            'If I was the president, I would fix everything.',
            'Were governments to prioritise educational investment, the resulting improvements in workforce productivity could transform national economies within a generation.',
            'If they had done something, it would have been better.'
          ],
          answer: 'Were governments to prioritise educational investment, the resulting improvements in workforce productivity could transform national economies within a generation.',
          explanation: 'This uses a formal inverted second conditional ("Were governments to…"), nominalization ("investment," "improvements," "productivity"), and a clear cause-effect chain. It projects authority and academic sophistication.'
        },
        {
          text: 'Which sentence uses a cleft structure effectively?',
          options: [
            'What is important is that we study hard.',
            'It is the persistent underfunding of public schools that has widened the achievement gap between socioeconomic groups.',
            'The thing that matters is money.',
            'What I think is that education should be free.'
          ],
          answer: 'It is the persistent underfunding of public schools that has widened the achievement gap between socioeconomic groups.',
          explanation: 'This it-cleft effectively emphasises "persistent underfunding" as the cause, uses academic vocabulary ("achievement gap," "socioeconomic groups"), and nominalization ("underfunding"). It demonstrates both structural complexity and lexical precision.'
        }
      ]
    }
  ],
  tips: [
    'Aim for 3-4 different complex structures per essay — variety matters more than quantity.',
    'Master nominalization first — it has the biggest impact on your formality and score.',
    'Always check that participle clause subjects match the main clause subject to avoid dangling modifiers.',
    'Use cleft sentences sparingly (1-2 per essay) for your strongest points — overuse dilutes their impact.'
  ],
  dailyPractice: [
    'Take 5 simple sentences from any text and rewrite each using a different complex structure.',
    'Practise converting one paragraph from verb-heavy to noun-heavy style using nominalization.',
    'Write 3 it-cleft and 3 what-cleft sentences on a current topic to build fluency.',
    'Read a Band 9 model essay and annotate every complex structure you can identify (relative clauses, passives, clefts, conditionals).'
  ],
  backPath: '/exams/english/#writing',
  backLabel: 'Back to Writing',
  aiContext: 'Complex Sentence Patterns for Writing course targeting IELTS Band 8-9. Covers relative clauses, participle clauses, nominalization, cleft sentences, conditional structures, and passive voice in academic writing. Teaches students to demonstrate the "wide range of grammatical structures used accurately" criterion.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...complexSentenceWritingData,
      title: complexSentenceWritingData.title + ' (Foundations)',
      overview: { ...complexSentenceWritingData.overview, duration: '15-20 minutes' },
      sections: complexSentenceWritingData.sections.slice(0, Math.max(2, Math.ceil(complexSentenceWritingData.sections.length / 2))),
      exercises: complexSentenceWritingData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...complexSentenceWritingData,
      title: complexSentenceWritingData.title + ' (Advanced)',
      overview: { ...complexSentenceWritingData.overview, duration: '30-35 minutes' },
      sections: complexSentenceWritingData.sections,
      exercises: complexSentenceWritingData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(complexSentenceWritingData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return complexSentenceWritingData;
}
